import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, CheckCircle2, Clock, AlertCircle, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { db } from '../../../lib/db';
import { formatDate, getDaysUntil, isOverdue, isDueSoon } from '../../../lib/dateUtils';
import { formatStatus, getStatusBadgeColor, getScoreLabel, getScoreColor } from '../../../lib/calculations';
import { useAuthStore } from '../../../store/authStore';
import AIAnalysisSummary from '../../shared/AIAnalysisSummary';

export default function MyAppraisalsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const detailBasePath = user?.role === 'supervisor' ? '/supervisor/my-appraisals' : '/employee/appraisals';
  const [statusFilter, setStatusFilter] = useState('all');
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, [user]);

  const loadData = async () => {
    try {
      const appraisalsData = await db.appraisals.toArray();
      setAppraisals(appraisalsData.filter(a => a.userId === user?.id));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const myAppraisals = appraisals;
  const filteredAppraisals = myAppraisals.filter(appraisal => statusFilter === 'all' || appraisal.status === statusFilter);
  const pendingAppraisals = myAppraisals.filter(a => a.status !== 'finalized');
  const completedAppraisals = myAppraisals.filter(a => a.status === 'finalized');

  const getStatusIcon = (status) => {
    switch (status) {
      case 'finalized': return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'employee_completed': return <Clock className="w-5 h-5 text-amber-500" />;
      default: return <AlertCircle className="w-5 h-5 text-blue-500" />;
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><span className="text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Appraisals</h1>
        <p className="text-gray-500 mt-1">View and complete your performance evaluations</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Appraisals</p>
                <p className="text-3xl font-bold text-gray-900">{myAppraisals.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center"><AlertCircle className="w-6 h-6 text-blue-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">In Progress</p>
                <p className="text-3xl font-bold text-gray-900">{pendingAppraisals.length}</p>
              </div>
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center"><Clock className="w-6 h-6 text-amber-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Completed</p>
                <p className="text-3xl font-bold text-gray-900">{completedAppraisals.length}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center"><CheckCircle2 className="w-6 h-6 text-emerald-600" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48"><Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Filter by status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Appraisals</SelectItem>
                <SelectItem value="ready_to_evaluate">Ready to Evaluate</SelectItem>
                <SelectItem value="employee_completed">Pending Review</SelectItem>
                <SelectItem value="finalized">Completed</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-500">{filteredAppraisals.length} appraisal(s)</p>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {filteredAppraisals.map(appraisal => {
          const daysUntil = getDaysUntil(appraisal.dueDate);
          const overdue = isOverdue(appraisal.dueDate) && appraisal.status !== 'finalized';
          const dueSoon = isDueSoon(appraisal.dueDate, 7) && appraisal.status !== 'finalized';
          return (
            <Card key={appraisal.id} className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer" onClick={() => navigate(`${detailBasePath}/${appraisal.id}`)}>
              <CardContent className="pt-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    {getStatusIcon(appraisal.status)}
                    <div>
                      <h3 className="font-semibold text-gray-900">{appraisal.title}</h3>
                      <p className="text-sm text-gray-500 capitalize">{appraisal.type} Review</p>
                      <div className="flex items-center gap-3 mt-2">
                        <Badge className={getStatusBadgeColor(appraisal.status)}>{formatStatus(appraisal.status)}</Badge>
                        {appraisal.status !== 'finalized' && (
                          <span className={`text-xs ${overdue ? 'text-rose-600 font-medium' : dueSoon ? 'text-amber-600' : 'text-gray-400'}`}>
                            {overdue ? `Overdue by ${Math.abs(daysUntil)} days` : `Due in ${daysUntil} days`}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="w-32">
                      <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Progress</span><span>{appraisal.progress?.overallPercent || 0}%</span></div>
                      <Progress value={appraisal.progress?.overallPercent || 0} className="h-2" />
                    </div>
                    {appraisal.scores?.finalWeightedScore && (
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Final Score</p>
                        <p className={`text-xl font-bold ${getScoreColor(appraisal.scores.finalWeightedScore)}`}>{appraisal.scores.finalWeightedScore.toFixed(1)}</p>
                        <p className="text-xs text-gray-400">{getScoreLabel(appraisal.scores.finalWeightedScore)}</p>
                      </div>
                    )}
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
                <AIAnalysisSummary appraisal={appraisal} />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredAppraisals.length === 0 && <div className="text-center py-12"><p className="text-gray-500">No appraisals found</p></div>}
    </div>
  );
}
